import { createVNode, isVNode, render, type VNodeChild } from 'vue'
import MessageView from './RrMessage.vue'
import type { MessageType } from '../../types'

export interface MessageOptions {
  content: VNodeChild
  duration?: number
  key?: string | number
  type?: MessageType
  onClose?: () => void
}
export interface MessageHandle extends PromiseLike<void> {
  (): void
  /** Project convenience; the returned handle is also callable as in Ant Design Vue. */
  close: () => void
}
interface MessageEntry { update: (options: MessageOptions) => void; handle: MessageHandle }
const entries = new Map<string | number | symbol, MessageEntry>()
let container: HTMLElement | null = null

function getContainer() {
  if (container) return container
  container = document.createElement('div')
  container.id = 'rr-message-container'
  container.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:var(--rr-z-message);pointer-events:none;display:flex;flex-direction:column;align-items:center;max-width:calc(100vw - 32px)'
  document.body.appendChild(container)
  return container
}

function open(options: MessageOptions): MessageHandle {
  const key = options.key ?? Symbol('rr-message')
  const existing = entries.get(key)
  if (existing) { existing.update(options); return existing.handle }
  let resolve!: () => void
  const closed = new Promise<void>(done => { resolve = done })
  let timer: ReturnType<typeof setTimeout> | undefined
  let wrapper: HTMLDivElement | undefined
  let current = options
  let isClosed = false
  const close = () => {
    if (isClosed) return
    isClosed = true
    if (timer !== undefined) clearTimeout(timer)
    entries.delete(key)
    if (wrapper) { render(null, wrapper); wrapper.remove() }
    if (!entries.size && container) { container.remove(); container = null }
    resolve()
    current.onClose?.()
  }
  const handle: MessageHandle = Object.assign(close, { close, then: closed.then.bind(closed) })
  // Messages are imperative client-side effects; SSR has no persistent DOM or timer to own.
  if (typeof document === 'undefined') { close(); return handle }
  wrapper = document.createElement('div')
  getContainer().appendChild(wrapper)
  function update(next: MessageOptions) {
    if (timer !== undefined) clearTimeout(timer)
    current = next
    render(createVNode(MessageView, { type: next.type ?? 'info', content: next.content }), wrapper!)
    const seconds = next.duration ?? 3
    if (seconds > 0) timer = setTimeout(close, seconds * 1000)
  }
  entries.set(key, { handle, update })
  update(options)
  return handle
}

function typed(type: MessageType, input: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) {
  const options = input && typeof input === 'object' && !isVNode(input) && !Array.isArray(input) && 'content' in input ? input as MessageOptions : { content: input as VNodeChild, duration, onClose }
  return open({ ...options, type })
}
export const message = {
  open,
  info: (content: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) => typed('info', content, duration, onClose),
  success: (content: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) => typed('success', content, duration, onClose),
  error: (content: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) => typed('error', content, duration, onClose),
  warning: (content: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) => typed('warning', content, duration, onClose),
  loading: (content: VNodeChild | MessageOptions, duration?: number, onClose?: () => void) => typed('loading', content, duration, onClose),
  destroy(key?: string | number) {
    if (key !== undefined) entries.get(key)?.handle()
    else for (const entry of [...entries.values()]) entry.handle()
  }
}
export type MessageApi = typeof message
export const RrMessage = message
